/**
 * Turns demo mode on: points every network path at the snapshot. Runs before React
 * mounts. No component or service is aware of it.
 *
 *   1. apiClient          -> apiClient.setAdapter()
 *   2. raw axios.get/post -> axios.defaults.adapter
 *   3. raw fetch()        -> window.fetch
 *
 * Path 1 needs its own call: axios.create() copies the adapter into the instance
 * defaults, and those win over axios.defaults.
 */

import axios, { type AxiosAdapter, type AxiosResponse } from 'axios';
import { apiClient } from '@rti/shared';
import { loadBundle, resolve, getMisses, type SnapshotBundle } from './snapshotStore';
import { seedFakeAuth, DEFAULT_DEMO_PERSONA, type DemoPersona } from './fakeAuth';
import { isDemoMode, markDemoMode } from './demoState';
import { installInteractionLock } from './interactionLock';

export { isDemoMode };

/** Captured before interception so the snapshot can still be loaded. */
const nativeFetch: typeof window.fetch =
  typeof window !== 'undefined' ? window.fetch.bind(window) : (undefined as never);

const nativeXHR = typeof window !== 'undefined' ? window.XMLHttpRequest : undefined;

export function isDemoPath(pathname: string = window.location.pathname): boolean {
  return pathname.startsWith('/demo/') || pathname === '/demo';
}

export function getNativeFetch(): typeof window.fetch {
  return nativeFetch;
}

const demoAdapter: AxiosAdapter = async (config) => {
  const result = resolve(
    config.method ?? 'get',
    config.url ?? '',
    (config.params ?? null) as Record<string, unknown> | null,
    config.baseURL,
  );

  return {
    data: result.data,
    status: result.status,
    statusText: result.hit ? 'OK' : 'OK (demo miss)',
    headers: {},
    config,
    request: null,
  } as unknown as AxiosResponse;
};

const demoFetch: typeof window.fetch = async (input, init) => {
  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : (input as Request).url;
  const method = init?.method ?? (input instanceof Request ? input.method : 'GET');

  const result = resolve(method, url, null);

  return new Response(JSON.stringify(result.data), {
    status: result.status,
    statusText: result.hit ? 'OK' : 'OK (demo miss)',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Tripwire. Nothing should reach XHR once the shims are in. */
function installEgressGuard(): void {
  if (!nativeXHR) return;

  class BlockedXHR extends nativeXHR {
    open(...args: unknown[]): void {
      const [method, url] = args as [string, string];
      console.error(`[demo] blocked XMLHttpRequest: ${method} ${url}`);
      throw new Error('Network access is disabled in demo mode');
    }
  }

  window.XMLHttpRequest = BlockedXHR as unknown as typeof XMLHttpRequest;

  // Exempted later for demo analytics only.
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    navigator.sendBeacon = () => {
      console.error('[demo] blocked sendBeacon');
      return false;
    };
  }
}

export interface InstallDemoModeOptions {
  bundle: SnapshotBundle;
  persona?: DemoPersona;
}

export function installDemoMode({
  bundle,
  persona = DEFAULT_DEMO_PERSONA,
}: InstallDemoModeOptions): void {
  if (isDemoMode()) return;

  loadBundle(bundle);

  apiClient.setAdapter(demoAdapter); 
  axios.defaults.adapter = demoAdapter; 
  window.fetch = demoFetch; 

  installEgressGuard();
  seedFakeAuth(persona);
  installInteractionLock(); // view-only: the tour drives, the user watches

  markDemoMode();

  // window.__demo.misses() lists unmapped requests, for authoring QA.
  (window as unknown as Record<string, unknown>).__demo = {
    misses: getMisses,
    persona,
    bundleLabel: bundle.label,
  };

  console.info(
    `[demo] active — ${Object.keys(bundle.entries ?? {}).length} snapshot entries, network disabled`,
  );
}
